import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGetSchoolTeacherQuery } from '../../store/api/schoolAdminApi'
import { useSchoolFilters } from '../../components/school/SchoolLayout'
import DateRangeBar from '../../components/school/DateRangeBar'
import { KpiCard, formatDate } from '../../components/school/SchoolUi'

const SchoolTeacherDetail: React.FC = () => {
  const { t } = useTranslation('school')
  const { userId } = useParams()
  const { filters } = useSchoolFilters()
  const { data, isLoading } = useGetSchoolTeacherQuery({ userId: Number(userId), ...filters })

  if (isLoading) return <div className="text-gray-500">{t('common.loading')}</div>
  if (!data) return <div className="text-gray-500">{t('teachers.empty')}</div>

  const teacher = data.teacher

  return (
    <div className="space-y-6">
      <div>
        <Link to="/pending/teachers" className="text-sm text-blue-600 hover:underline">{t('teachers.back')}</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">{teacher.name}</h1>
        <p className="text-gray-600">{t('teachers.detailTitle')} · {teacher.email}</p>
      </div>
      <DateRangeBar showClassFilter={false} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label={t('teachers.columns.classes')} value={teacher.classCount} />
        <KpiCard label={t('teachers.columns.activities')} value={teacher.activities} />
        <KpiCard label={t('teachers.columns.activeStudents')} value={teacher.activeStudents} />
        <KpiCard label={t('teachers.columns.aiSessions')} value={teacher.aiSessions} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b"><h2 className="font-semibold">{t('teachers.usageByClass')}</h2></div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('classes.columns.class')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('classes.columns.students')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('classes.columns.activities')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('classes.columns.aiSessions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(data.classes || []).map((c: any) => (
              <tr key={c.id}>
                <td className="px-4 py-3 text-sm">
                  <Link to={`/pending/classes/${c.id}`} className="text-blue-600 hover:underline">{c.name}</Link>
                </td>
                <td className="px-4 py-3 text-sm">{c.memberCount}</td>
                <td className="px-4 py-3 text-sm">{c.activities}</td>
                <td className="px-4 py-3 text-sm">{c.aiSessions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b"><h2 className="font-semibold">{t('teachers.recentActivities')}</h2></div>
        <ul className="divide-y">
          {(data.recentActivities || []).map((a: any) => (
            <li key={a.id} className="px-5 py-3 text-sm flex justify-between gap-4">
              <div>
                <Link to={`/pending/activities/${a.id}`} className="text-blue-600 hover:underline font-medium">{a.name}</Link>
                <div className="text-xs text-gray-500">{a.className} · {a.unitName}</div>
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(a.createdAt)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default SchoolTeacherDetail
